import jsPDF from 'jspdf';
import { format } from 'date-fns';

export const generateMarketingPDF = async (invoice) => {
  try {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let y = 0;

    const currencies = {
      USD: { symbol: '$' },
      EUR: { symbol: '€' },
      BRL: { symbol: 'R$' },
      GBP: { symbol: '£' }
    };
    const currencySymbol = currencies[invoice.currency]?.symbol || '$';

    const invoiceNumber = invoice.fullNumber || `MKT-${invoice.number}`;

    // --- Header ---
    doc.setFillColor(248, 249, 250);
    doc.rect(0, 0, pageWidth, 100, 'F');
    y = 50;

    if (invoice.companyInfo?.logoUrl) {
      try {
        const img = new Image();
        img.src = invoice.companyInfo.logoUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        
        const maxWidth = 80;
        const maxHeight = 80;
        const imgAspectRatio = img.width / img.height;
        const boxAspectRatio = maxWidth / maxHeight;

        let logoWidth;
        let logoHeight;

        if (imgAspectRatio > boxAspectRatio) {
          logoWidth = maxWidth;
          logoHeight = maxWidth / imgAspectRatio;
        } else {
          logoHeight = maxHeight;
          logoWidth = maxHeight * imgAspectRatio;
        }

        doc.addImage(invoice.companyInfo.logoUrl, 'PNG', margin, y - 20 + (maxHeight - logoHeight) / 2, logoWidth, logoHeight);
      } catch (e) {
        console.log('Logo could not be loaded.');
      }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.setTextColor(17, 24, 39);
    doc.text('INVOICE', pageWidth - margin, y + 15, { align: 'right' });

    // --- STATUS LABEL ---
    const status = invoice.status || 'unpaid';
    const isPaid = status === 'paid';
    const statusText = isPaid ? 'PAID' : 'UNPAID';
    const statusColor = isPaid ? [0, 128, 0] : [220, 38, 38];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.text(statusText, pageWidth - margin, y + 35, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(`Invoice #${invoiceNumber}`, pageWidth - margin, y + 50, { align: 'right' });
    doc.text(`Date: ${format(new Date(invoice.paymentDate || invoice.createdAt), 'MMMM dd, yyyy')}`, pageWidth - margin, y + 65, { align: 'right' });

    y = 140;

    // --- Billing Information ---
    const infoBoxWidth = (pageWidth - (margin * 2) - 30) / 2;

    // Bill To
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text('BILL TO', margin, y);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    doc.text(invoice.customer.name, margin, y + 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(55, 65, 81);
    const customerAddress = doc.splitTextToSize(invoice.customer.address || '', infoBoxWidth);
    doc.text(customerAddress, margin, y + 35);
    let customerInfoY = y + 35 + (customerAddress.length * 12);
    if (invoice.customer.email) {
      doc.text(invoice.customer.email, margin, customerInfoY);
    }

    // From
    const fromX = margin + infoBoxWidth + 30;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text('FROM', fromX, y);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    doc.text(invoice.companyInfo.name, fromX, y + 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(55, 65, 81);
    const companyAddress = doc.splitTextToSize(invoice.companyInfo.address || '', infoBoxWidth);
    doc.text(companyAddress, fromX, y + 35);
    let companyInfoY = y + 35 + (companyAddress.length * 12);
    if (invoice.companyInfo.email) {
      doc.text(invoice.companyInfo.email, fromX, companyInfoY);
    }

    y = Math.max(customerInfoY, companyInfoY) + 50;

    // --- Items Table ---
    const tableHeaders = ['Service Description', 'Qty', 'Unit Price', 'Amount'];
    const tableStartX = margin;
    const tableWidth = pageWidth - (margin * 2);
    const colWidths = [tableWidth * 0.55, tableWidth * 0.1, tableWidth * 0.15, tableWidth * 0.2];

    // Table Header
    doc.setFillColor(17, 24, 39);
    doc.rect(tableStartX, y, tableWidth, 25, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    
    let currentX = tableStartX;
    doc.text(tableHeaders[0], currentX + 10, y + 16);
    currentX += colWidths[0];
    doc.text(tableHeaders[1], currentX, y + 16, { align: 'center' });
    currentX += colWidths[1];
    doc.text(tableHeaders[2], currentX + colWidths[2], y + 16, { align: 'right' });
    currentX += colWidths[2];
    doc.text(tableHeaders[3], currentX + colWidths[3] - 10, y + 16, { align: 'right' });
    
    y += 25;

    // Table Body
    (invoice.items || []).forEach((item, index) => {
      const nameLines = doc.splitTextToSize(item.name || 'Service', colWidths[0] - 15);
      const descLines = doc.splitTextToSize(item.description || '', colWidths[0] - 15);
      const rowHeight = Math.max(35, (nameLines.length * 12) + (descLines.length * 10) + 15);

      if (index % 2 !== 0) {
        doc.setFillColor(248, 249, 250);
        doc.rect(tableStartX, y, tableWidth, rowHeight, 'F');
      }

      let lineY = y + 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(17, 24, 39);
      doc.text(nameLines, tableStartX + 10, lineY);
      lineY += nameLines.length * 12;

      if (descLines.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128);
        doc.text(descLines, tableStartX + 10, lineY);
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(55, 65, 81);
      
      let colX = tableStartX + colWidths[0];
      doc.text(String(item.quantity), colX, y + rowHeight / 2, { align: 'center' });
      colX += colWidths[1];
      doc.text(`${currencySymbol}${item.price.toFixed(2)}`, colX + colWidths[2], y + rowHeight / 2, { align: 'right' });
      colX += colWidths[2];
      doc.text(`${currencySymbol}${item.total.toFixed(2)}`, colX + colWidths[3] - 10, y + rowHeight / 2, { align: 'right' });

      y += rowHeight;
    });

    // --- Totals ---
    const totalsX = pageWidth / 2;
    const totalsWidth = pageWidth / 2 - margin;
    y += 20;

    const addTotalLine = (label, value, isBold = false) => {
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setFontSize(isBold ? 12 : 10);
      doc.setTextColor(isBold ? 17 : 55, isBold ? 24 : 65, isBold ? 39 : 81);
      doc.text(label, totalsX, y);
      doc.text(value, pageWidth - margin, y, { align: 'right' });
      y += isBold ? 22 : 18;
    };

    addTotalLine('Subtotal', `${currencySymbol}${invoice.subtotal.toFixed(2)}`);
    if (invoice.processingFee > 0) {
      addTotalLine('Processing Fee', `${currencySymbol}${invoice.processingFee.toFixed(2)}`);
    }
    
    y += 5;
    doc.setDrawColor(229, 231, 235);
    doc.line(totalsX, y, pageWidth - margin, y);
    y += 15;

    addTotalLine('Total', `${currencySymbol}${invoice.total.toFixed(2)}`, true);

    // --- Notes ---
    if (invoice.notes && invoice.notes.trim()) {
      y = Math.max(y, 200);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text('NOTES', margin, y);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(55, 65, 81);
      const noteLines = doc.splitTextToSize(invoice.notes, pageWidth / 2 - margin - 20);
      doc.text(noteLines, margin, y + 15);
    }

    // --- Footer ---
    const footerY = doc.internal.pageSize.getHeight() - 30;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text('Thank you for your business!', pageWidth / 2, footerY, { align: 'center' });

    // --- Save ---
    const filename = `Invoice_${invoiceNumber.replace('-', '_')}_${invoice.customer.name.replace(/ /g, '_')}.pdf`;
    doc.save(filename);

  } catch (error) {
    console.error('Error generating marketing PDF:', error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};